import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal.js';
import { WizardProgress } from './WizardProgress.js';
import { WelcomeStep } from './WelcomeStep.js';
import { ComfortStep } from './ComfortStep.js';
import { FamilyStep } from './FamilyStep.js';
import { CompletionStep } from './CompletionStep.js';
import { useAuthStore } from '../../stores/authStore.js';

const TOTAL_STEPS = 4;

export function WelcomeWizard() {
  const { i18n } = useTranslation();
  const { user, wizardDismissed, dismissWizard, completeOnboarding } = useAuthStore();
  const [step, setStep] = useState(0);
  const [joinedFamily, setJoinedFamily] = useState(false);
  const currentLang = i18n.language?.startsWith('ga') ? 'ga' : 'en';

  const handleLanguageChange = useCallback((lang: 'ga' | 'en') => {
    void i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
  }, [i18n]);

  const shouldShow = user && !user.hasCompletedOnboarding && !wizardDismissed;

  const handleDismiss = useCallback(() => {
    dismissWizard();
  }, [dismissWizard]);

  const handleComplete = useCallback(async () => {
    await completeOnboarding();
  }, [completeOnboarding]);

  if (!shouldShow) return null;

  return (
    <Modal
      open
      onClose={handleDismiss}
      size="lg"
      showCloseButton
      closeOnBackdrop
      closeOnEscape
    >
      {/* Language toggle */}
      <div className="flex justify-center mb-4">
        <div className="flex rounded-lg bg-surface-3 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => { handleLanguageChange('ga'); }}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              currentLang === 'ga' ? 'bg-primary text-primary-on' : 'text-ink-3 hover:text-ink-1'
            }`}
          >
            Gaeilge
          </button>
          <button
            type="button"
            onClick={() => { handleLanguageChange('en'); }}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              currentLang === 'en' ? 'bg-primary text-primary-on' : 'text-ink-3 hover:text-ink-1'
            }`}
          >
            English
          </button>
        </div>
      </div>

      <WizardProgress currentStep={step} totalSteps={TOTAL_STEPS} />

      {step === 0 && (
        <WelcomeStep onNext={() => { setStep(1); }} />
      )}

      {step === 1 && (
        <ComfortStep onNext={() => { setStep(2); }} />
      )}

      {step === 2 && (
        <FamilyStep
          onNext={() => {
            setJoinedFamily(true);
            setStep(3);
          }}
          onSkip={() => { setStep(3); }}
        />
      )}

      {step === 3 && (
        <CompletionStep
          hasFamily={joinedFamily}
          onComplete={handleComplete}
        />
      )}
    </Modal>
  );
}
